import { html, LitElement } from 'lit';
import { nothing } from 'lit-html';
import fsTypography from '@cof/fs-typography-core/lib/typography.lit.min.js';
import { TagKey } from '@cof/tag-catalog-fs-vehicle-and-loan-mfe';
import { DebugService } from '../shared/services/debug.service.js';
import { style } from './iconic-vehicle-and-loan-container.style.js';
import { globalStyle } from '../global-styles.style.js';
import { REFI_VEHICLE_LOAN_CONST } from '../shared/constants/app.constants.js';
import {
  showBackButtonEvent,
  stateChangedEvent,
} from './utils/host-app-events.js';
import './iconic-vehicle-loan/iconic-vehicle-loan.component.js';
import './iconic-vehicle/iconic-choose-vehicle/iconic-choose-vehicle.component.js';
import { ApplicationService } from '../services/application.service.js';
import { VehicleAndLoanService } from '../services/vehicle-and-loan.service.js';
import { AppService } from '../app.service.js';
import './iconic-confirm/iconic-confirm.component.js';
import './iconic-container/iconic-container.component.js';
import './iconic-exit/iconic-exit.component.js';
import './delayed-decline/delayed-decline.component.js';
import './vehicle-loan/vehicle-consent/vehicle-consent.component.js';
import { stratum } from '../../constants/stratum.js';
import { PerformanceTrackingService } from '../services/performanceTracking.service.js';

class RefiIconicVehicleAndLoanContainer extends LitElement {
  MFE_NAME = 'vehicle-and-loan';

  constructor() {
    super();
    this.debug = new DebugService('RefiIconicVehicleAndLoanContainer');
    this.application = {};
    this.currentSteps = [];
    this.currentStepIndex = 0;
    this.vehicleAndLoanList = [];
    this.hashChangeBinded = this.onHashChange.bind(this);
    this.loading = true;
    this.showLookup = false;
    this.loadingWithoutAnimation = false;
    this.exitPageFromManualVehicleSearch = false;
  }

  static properties = {
    application: { type: Object, reflect: true },
    steps: { type: Array, reflect: true },
    currentStepIndex: { type: Number, reflect: true },
    loading: { type: Boolean, state: true },
    loadingWithoutAnimation: { type: Boolean, state: true },
  };

  static styles = [style, globalStyle, fsTypography];

  connectedCallback() {
    super.connectedCallback();
    this.setup();
    window.addEventListener('hashchange', this.hashChangeBinded);
    // window.addEventListener('locationchange', () => {
    //   this.debug.log(window.location);
    // });
    stateChangedEvent(this, this.currentSteps, this.currentStepIndex, true);
    showBackButtonEvent(
      this,
      !(ApplicationService.isUpfrontOffers && this.currentStepIndex === 0)
    );
    this.lighthouseSetup();
    VehicleAndLoanService.registerLoadingListener((status) => {
      if (!status) {
        this.setup();
        stateChangedEvent(this, this.currentSteps, this.currentStepIndex, true);
        this.loading = false;
        PerformanceTrackingService.performanceTrackStop(
          'vehicle search after consent'
        );
        if (VehicleAndLoanService.vehicleList.length > 0) {
          this.dispatchEvent(
            new CustomEvent('show-completion-animation', { detail: true })
          );
        }
        this.dispatchEvent(new CustomEvent('initiate-animation-completion'));
        this.requestUpdate();
      }
    });
    this.loading = false;
    stratum.publishTag(TagKey.PAGE_VIEW_COAF_REFI_PREQUALIFY_VEHICLE_LOAN_2);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('hashchange', this.hashChangeBinded);
  }

  async lighthouseSetup() {
    if (AppService.testingParam) {
      this.getVehicleAndLoanSearch().then(() => {
        this.currentStepIndex =
          REFI_VEHICLE_LOAN_CONST.LIGHTHOUSE_QUERY_PARAM[
            AppService.testingParam
          ].stepIndex;
        stateChangedEvent(this, this.currentSteps, this.currentStepIndex, true);
      });
    }
  }

  setup() {
    this.applicant = ApplicationService.applicant;
    this.application = ApplicationService.Application;

    if (ApplicationService.isInAppDeclineDelayOn) {
      this.currentSteps = REFI_VEHICLE_LOAN_CONST.IN_APP_DECLINE_DELAY_STEPS(
        ApplicationService.hasConsent,
        VehicleAndLoanService.showMatches
      );
    } else {
      this.currentSteps = REFI_VEHICLE_LOAN_CONST.ICONIC_STEPS(
        ApplicationService.hasConsent,
        VehicleAndLoanService.showMatches,
        !this.checkIfAnyVehicleLoanIsValid()
      );
    }
  }

  render() {
    return !this.loading && !this.loadingWithoutAnimation
      ? html` <div>
          ${this.renderPage(this.currentSteps[this.currentStepIndex])}
          <refi-iconic-container
            >${this.renderDisclosure()}
          </refi-iconic-container>
        </div>`
      : ApplicationService.isInAppDeclineDelayOn
      ? html`<refi-loading-spinner
          message="${REFI_VEHICLE_LOAN_CONST.SEARCH_TEXT}"
        ></refi-loading-spinner>`
      : '';
  }

  renderDisclosure() {
    const doesntLookRightDisclosure = html`<fs-body-text
      size="small"
      variant="secondary"
      >Something not look right?
      <a
        href="https://www.capitalone.com/help-center/credit-cards/disputing-items-on-your-credit-report/"
        target="_blank"
        rel="noopener noreferrer"
        class="fs-link fs-link--primary"
        @click="${() =>
          ApplicationService.isInAppDeclineDelayOn
            ? stratum.publishTag(
                TagKey.ICONIC_VEHICLE_AND_LOAN_CREDIT_DISPUTE_LINK
              )
            : ''}"
        >Learn how to dispute it.</a
      ></fs-body-text
    >`;
    const businessPurposeCarDisclosure = html`<fs-body-text
      size="small"
      variant="secondary"
      >We don't refinance cars used for business purposes, including ride
      sharing (like Uber or Lyft).
      <a
        href="https://www.capitalone.com/auto-financing/refinance/faq/"
        target="_blank"
        rel="noopener noreferrer"
        class="fs-link fs-link--primary"
        >Check our eligibility requirements</a
      >
      to make sure we can refinance the car you pick.</fs-body-text
    >`;

    if (
      this.currentSteps[this.currentStepIndex] === 'confirm' ||
      this.currentSteps[
        this.currentStepIndex ===
          REFI_VEHICLE_LOAN_CONST.VEHICLE_AND_LOAN_BASIC_PAGES.CONFIRM
      ]
    ) {
      return html`${ApplicationService.isInAppDeclineDelayOn
          ? html`${doesntLookRightDisclosure}<br />`
          : ''}<fs-body-text size="small" variant="secondary"
          >After this step, your car and connected loan info will be locked. If
          you need to make further changes to your selected car or loan, you
          will have to start a new application. Have questions? Call
          <a href="tel:8777334227" class="fs-link fs-link--primary"
            >877-733-4227</a
          >.</fs-body-text
        >`;
    }

    if (
      this.currentSteps[this.currentStepIndex] === 'exit' ||
      this.currentSteps === REFI_VEHICLE_LOAN_CONST.PAGES.VEHICLE_CONSENT
    ) {
      return '';
    }
    if (ApplicationService.isInAppDeclineDelayOn) {
      if (
        this.currentSteps[this.currentStepIndex] ===
          REFI_VEHICLE_LOAN_CONST.VEHICLE_AND_LOAN_BASIC_PAGES.VEHICLE_LOAN ||
        this.currentSteps[this.currentStepIndex] ===
          REFI_VEHICLE_LOAN_CONST.PAGES.CONFIRM
      ) {
        return html`<br />
          ${doesntLookRightDisclosure}`;
      }
      if (
        this.currentSteps[this.currentStepIndex] ===
        REFI_VEHICLE_LOAN_CONST.VEHICLE_AND_LOAN_BASIC_PAGES.CHOOSE_VEHICLE
      ) {
        return html`${businessPurposeCarDisclosure}`;
      }
      if (
        this.currentSteps[this.currentStepIndex] ===
        REFI_VEHICLE_LOAN_CONST.VEHICLE_AND_LOAN_BASIC_PAGES.LOAN
      ) {
        return ``;
      }
      return '';
    }
    return html`${doesntLookRightDisclosure}
      <br />
      ${businessPurposeCarDisclosure}`;
  }

  async getVehicleAndLoanSearch() {
    const { vehicleAndLoanMatchList, vehicleList, loanList } =
      VehicleAndLoanService;
    if (
      vehicleAndLoanMatchList.length !== 0 ||
      vehicleList.length !== 0 ||
      loanList.length !== 0
    ) {
      this.vehicleAndLoanList = VehicleAndLoanService.vehicleAndLoanMatchList;
      VehicleAndLoanService.showMatches = this.vehicleAndLoanList.length !== 0;
    }
    if (ApplicationService.isInAppDeclineDelayOn) {
      this.currentSteps = REFI_VEHICLE_LOAN_CONST.IN_APP_DECLINE_DELAY_STEPS(
        ApplicationService.hasConsent,
        VehicleAndLoanService.showMatches
      );
    } else {
      this.currentSteps = REFI_VEHICLE_LOAN_CONST.ICONIC_STEPS(
        ApplicationService.hasConsent,
        VehicleAndLoanService.showMatches,
        false
      );
    }
  }

  renderPage(pageName) {
    this.debug.log('rendering on ', pageName);
    if (pageName === undefined) {
      stateChangedEvent(this, this.currentSteps, 0);
      return nothing;
    }

    this.debug.log(
      `InApp Decline Delay test ${
        ApplicationService.isInAppDeclineDelayOn
          ? 'is active. Using delayed decline page list.'
          : 'is not active. Using pcyrea page list. '
      }`
    );
    if (ApplicationService.isInAppDeclineDelayOn) {
      switch (pageName) {
        case REFI_VEHICLE_LOAN_CONST.PAGES.VEHICLE_CONSENT:
          return html` <refi-iconic-container>
            <refi-vehicle-consent
              @next-step="${this.determinePageFlow}"
            ></refi-vehicle-consent>
          </refi-iconic-container>`;
        case REFI_VEHICLE_LOAN_CONST.PAGES.VEHICLE_LOAN:
          return html` <refi-iconic-vehicle-loan
            @next-step="${this.nextStep}"
            @submit-vehicle-loan="${this.submitVehicleLoan}"
            @go-to-review="${this.submitVehicleLoan}"
            @delayed-decline="${this.goToDecline}"
          ></refi-iconic-vehicle-loan>`;
        case REFI_VEHICLE_LOAN_CONST.VEHICLE_AND_LOAN_BASIC_PAGES
          .CHOOSE_VEHICLE:
          return html` <refi-iconic-choose-vehicle
            @next-step="${this.nextStep}"
            .showVehicleLookup="${false}"
          ></refi-iconic-choose-vehicle>`;
        case REFI_VEHICLE_LOAN_CONST.VEHICLE_AND_LOAN_BASIC_PAGES.LOAN:
          return html` <refi-iconic-choose-loan
            @next-step="${this.nextStep}"
            @reset-flow="${this.resetFlow}"
            @submit-vehicle-loan="${this.submitVehicleLoan}"
            @go-to-review="${this.nextStep}"
            @go-to-vehicle="${this.goToVehicle}"
          ></refi-iconic-choose-loan>`;
        case REFI_VEHICLE_LOAN_CONST.VEHICLE_AND_LOAN_BASIC_PAGES.CONFIRM:
          return html` <refi-iconic-confirm
            @go-to-vehicle="${this.goToVehicle}"
            @submit-vehicle-loan="${this.submitVehicleLoan}"
            @delayed-decline="${this.nextStep}"
          ></refi-iconic-confirm>`;
        case REFI_VEHICLE_LOAN_CONST.INAPP_DECLINE_DELAY_PAGES.DECLINE:
          return html` <refi-delayed-decline
            @go-to-vehicle="${this.goToVehicle}"
          ></refi-delayed-decline>`;
        default:
          return nothing;
      }
    } else {
      switch (pageName) {
        case REFI_VEHICLE_LOAN_CONST.PAGES.VEHICLE_CONSENT:
          return html` <refi-iconic-container>
            <refi-vehicle-consent
              @next-step="${this.determinePageFlow}"
            ></refi-vehicle-consent>
          </refi-iconic-container>`;
        case REFI_VEHICLE_LOAN_CONST.PAGES.VEHICLE_LOAN:
          return html`<refi-iconic-vehicle-loan
            @next-step="${this.nextStep}"
            @go-to-review="${this.submitVehicleLoan}"
          ></refi-iconic-vehicle-loan>`;
        case REFI_VEHICLE_LOAN_CONST.VEHICLE_AND_LOAN_BASIC_PAGES
          .CHOOSE_VEHICLE:
          return html`<refi-iconic-choose-vehicle
            .showVehicleLookup=${this.showLookup}
            @next-step="${this.nextStep}"
            @go-to-exit="${this.goToExit}"
          ></refi-iconic-choose-vehicle>`;
        case REFI_VEHICLE_LOAN_CONST.VEHICLE_AND_LOAN_BASIC_PAGES.LOAN:
          return html`<refi-iconic-choose-loan
            .useManualEntry=${this.showLookup}
            @next-step="${this.nextStep}"
            @go-to-vehicle="${this.goToVehicle}"
            @go-to-review="${this.goToConfirm}"
          ></refi-iconic-choose-loan>`;
        case REFI_VEHICLE_LOAN_CONST.VEHICLE_AND_LOAN_BASIC_PAGES.CONFIRM:
          return html`<refi-iconic-confirm
            @go-to-vehicle="${this.goToVehicle}"
            @submit-vehicle-loan="${this.submitVehicleLoan}"
          ></refi-iconic-confirm>`;
        case REFI_VEHICLE_LOAN_CONST.EXIT_PAGE.EXIT:
          return html`<refi-iconic-exit
            @go-to-vehicle="${this.goToVehicle}"
            @go-to-loan="${this.goToLoan}"
            ?from-vehicle-search="${this.exitPageFromManualVehicleSearch}"
          ></refi-iconic-exit>`;
        default:
          return nothing;
      }
    }
  }

  firstUpdated() {
    PerformanceTrackingService.performanceTrackStop('vehicle');
    if (
      !AppService.testingParam &&
      this.currentStepIndex + 1 !== this.currentSteps.length &&
      (!this.getCurrentHash() || !this.currentSteps[this.getCurrentHash()])
    ) {
      stateChangedEvent(this, this.currentSteps, 0);
    }
  }

  stateChangedEvent() {
    if (
      !AppService.testingParam &&
      this.currentStepIndex + 1 !== this.currentSteps.length &&
      (!this.getCurrentHash() || !this.currentSteps[this.getCurrentHash()])
    ) {
      stateChangedEvent(this, this.currentSteps, 0);
    }
  }

  async determinePageFlow() {
    PerformanceTrackingService.performanceTrackStart(
      'vehicle search after consent'
    );
    // this.loadingWithoutAnimation = true;
    this.dispatchEvent(new CustomEvent('start-animation'));
    await VehicleAndLoanService.searchVehicleAndLoan()
      .then(() => {
        this.dispatchEvent(new CustomEvent('stop-animation'));
      })
      .catch(() => {
        this.dispatchEvent(new CustomEvent('stop-animation'));
      });
    // this.loading = false;
    // this.loadingFromConsent = false;
    this.nextStep();
  }

  resetFlow() {
    stateChangedEvent(this, this.currentSteps, 0);
    VehicleAndLoanService.resetVehicleAndLoanService();
  }

  goToVehicle(ev) {
    this.exitPageFromManualVehicleSearch = false;
    this.showLookup = ev?.detail;
    if (ApplicationService.isInAppDeclineDelayOn) {
      this.showMatches = false;
    }
    stateChangedEvent(
      this,
      this.currentSteps,
      this.currentSteps.findIndex((step) => step === 'vehicle')
    );
  }

  goToConfirm() {
    stateChangedEvent(
      this,
      this.currentSteps,
      this.currentSteps.findIndex((step) => step === 'confirm')
    );
  }

  goToLoan(ev) {
    this.showLookup = ev?.detail;
    stateChangedEvent(
      this,
      this.currentSteps,
      this.currentSteps.findIndex((step) => step === 'loan')
    );
  }

  goToExit() {
    this.exitPageFromManualVehicleSearch = true;
    stateChangedEvent(
      this,
      this.currentSteps,
      this.currentSteps.findIndex((step) => step === 'exit')
    );
  }

  goToDecline() {
    if (ApplicationService.isInAppDeclineDelayOn) {
      stateChangedEvent(
        this,
        this.currentSteps,
        this.currentSteps.findIndex((step) => step === 'ineligible')
      );
    }
  }

  submitVehicleLoan() {
    this.debug.log('vehicle-loan to next-mfe event');
    const application = { ...ApplicationService.Application };
    if (ApplicationService.isInAppDeclineDelayOn) {
      application.selectedLoan.ineligibleReasons = [];
      application.selectedLoan.isEligible = true;
    }
    this.dispatchEvent(
      new CustomEvent('host-app-event', {
        bubbles: true,
        composed: true,
        detail: {
          actionName: 'next-mfe',
          data: { application },
        },
      })
    );
  }

  nextStep(event) {
    this.debug.log('application', ApplicationService.Application);
    const skipUpfrontOffers = event?.detail?.skipUpfrontOffers;
    const lastPage = ApplicationService.isInAppDeclineDelayOn
      ? this.currentStepIndex === this.currentSteps.length
      : this.currentSteps[this.currentStepIndex] === 'confirm';
    if (lastPage) {
      this.debug.log('sending next-mfe event to host app');
      if (skipUpfrontOffers) {
        this.debug.log('skipping upfront offers...');
        this.dispatchEvent(
          new CustomEvent('skip-upfront-offers', {
            bubbles: true,
            composed: true,
            detail: {
              data: {
                application: ApplicationService.Application,
              },
            },
          })
        );
      } else {
        this.dispatchEvent(
          new CustomEvent('host-app-event', {
            bubbles: true,
            composed: true,
            detail: {
              actionName: 'next-mfe',
              data: { application: ApplicationService.Application },
            },
          })
        );
      }
    } else {
      this.debug.log('next-step');
      stateChangedEvent(this, this.currentSteps, this.currentStepIndex + 1);
      if (event?.detail === true) {
        this.showLookup = event.detail;
      }
      this.loading = false;
      this.loadingWithoutAnimation = false;
    }
  }

  onHashChange() {
    const { hash } = window.location;
    const [, poppedMfeName, poppedState] = hash.split('/');
    this.debug.log('onHashChange', poppedState);

    if (
      !AppService.testingParam &&
      this.currentStepIndex < this.currentSteps.length &&
      poppedState !== this.currentSteps[this.currentStepIndex]
    ) {
      this.debug.log(
        'updating current step index',
        this.getCurrentHash(),
        this.currentStepIndex
      );
      // host app will handle MFE name change on hash.
      if (
        poppedMfeName === this.MFE_NAME &&
        poppedState !== this.currentSteps[this.currentStepIndex]
      ) {
        this.debug.log('on hash change, hash not in steps: ', poppedState);
        this.currentStepIndex = this.currentSteps.indexOf(poppedState);
        stateChangedEvent(this, this.currentSteps, this.currentStepIndex);
        showBackButtonEvent(
          this,
          !(ApplicationService.isUpfrontOffers && this.currentStepIndex === 0)
        );
      }
    }
  }

  getCurrentHash() {
    const { hash } = window.location;
    const [, , poppedState] = hash.split('/');
    return poppedState;
  }

  checkIfAnyVehicleLoanIsValid() {
    const { vehicleAndLoanMatchList, vehicleList, loanList } =
      VehicleAndLoanService;
    if (vehicleAndLoanMatchList.length > 0) {
      return VehicleAndLoanService.eligibleMatchCount() > 0;
    }
    if (vehicleList.length > 0) {
      let isValid = false;
      vehicleList.forEach((vehicle) => {
        if (VehicleAndLoanService.isVehicleEligible(vehicle)) {
          isValid = true;
        }
      });
      return isValid;
    }
    if (loanList.length > 0) {
      let isValid = false;
      loanList.forEach((loan) => {
        if (VehicleAndLoanService.isLoanEligible(loan)) {
          isValid = true;
        }
      });
      return isValid;
    }
    return true;
  }
}

customElements.define(
  'refi-iconic-vehicle-and-loan-container',
  RefiIconicVehicleAndLoanContainer
);
